import React from "react";
import {
  Button,
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import "./LoginPage.css";
import loginShape from "../../assets/login-shape.svg";
import cozciLogo from "../../assets/cozci-logo.svg";
import loginContainer from "../../assets/login-container.svg";
import GradientBorderAutocomplete from "./components/GradientBorderAutocomplete";
import GradientBorderPasswordField from "./components/GradientBorderPasswordField";
import { useLoginPage } from "./useLoginPage";

const LoginPage: React.FC = () => {
  const {
    email,
    setEmail,
    companyName,
    setCompanyName,
    password,
    setPassword,
    showLoginPassword,
    error,
    isResetModalOpen,
    setIsResetModalOpen,
    resetEmail,
    setResetEmail,
    resetCompanyName,
    setResetCompanyName,
    resetMessage,
    isSubmitting,
    cooldown,
    isLoggingIn,
    toggleShowLoginPassword,
    handleRequestReset,
    handleSubmit,
  } = useLoginPage();

  return (
    <div className="login-page-background">
      <img src={loginShape} alt="background shape" className="login-shape" />
      <img src={cozciLogo} alt="COZCI Logo" className="login-logo" />
      <div className="login-main-content">
        <div className="login-container-wrapper">
          <img
            src={loginContainer}
            alt="Login Background"
            className="login-background-svg"
          />
          <div className="login-content">
            <form
              onSubmit={handleSubmit}
              className="login-form"
              autoComplete="on"
            >
              {/* ... form inputs ... */}
              <div className="input-group">
                <label htmlFor="email">メールアドレス</label>
                <GradientBorderAutocomplete
                  options={[]}
                  inputValue={email}
                  onInputChange={(event, newInputValue) =>
                    setEmail(newInputValue)
                  }
                  label=""
                  width={390}
                  autocomplete="email"
                />
              </div>
              <div className="input-group">
                <label htmlFor="companyName">企業名</label>
                <GradientBorderAutocomplete
                  options={[]} // 企業名のオプションリストを追加する場合
                  inputValue={companyName}
                  onInputChange={(event, newInputValue) =>
                    setCompanyName(newInputValue)
                  }
                  label=""
                  width={390}
                  autocomplete="organization"
                />
              </div>
              <div className="input-group">
                <label htmlFor="password">パスワード</label>
                <GradientBorderPasswordField
                  value={password}
                  onChange={setPassword}
                  label=""
                  width={390}
                  showPassword={showLoginPassword}
                  onTogglePassword={toggleShowLoginPassword}
                  autocomplete="current-password"
                />
              </div>
              {error && <p className="error-message">{error}</p>}
              <button
                type="submit"
                className="login-button"
                disabled={isLoggingIn}
              >
                ログイン
              </button>
            </form>
          </div>
        </div>
        <div className="forgot-password-link">
          <button
            type="button"
            style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={(e) => {
              e.preventDefault();
              setResetEmail(email);
              setResetCompanyName(companyName);
              setIsResetModalOpen(true);
            }}
          >
            パスワードをお忘れの方はこちら
          </button>
        </div>
      </div>
      {isResetModalOpen && (
        <div className="modal-overlay">
          <Card
            sx={{
              position: "relative",
              width: 500, // Adjusted for content
              p: 4,
              borderRadius: "12px",
              outline: "none",
              "::before": {
                content: "''", // Updated to explicitly quoted empty string
                position: "absolute",
                inset: "4px",
                border: "2px solid rgba(115, 169, 244, 1)",
                pointerEvents: "none",
                borderRadius: "12px",
              },
            }}
          >
            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
              <Box
                sx={{
                  pb: 1,
                  mb: 2,
                  borderBottom: "2px solid rgba(115, 169, 244, 1)",
                  width: "calc(100% - 2px)",
                  mx: "auto",
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{
                    textAlign: "center",
                    color: "rgba(115, 169, 244, 1)",
                    fontWeight: "bold",
                  }}
                >
                  パスワードリセット
                </Typography>
              </Box>
              <Box
                sx={{ mt: 2, p: "0 16px" /* Add some horizontal padding */ }}
              >
                <Typography variant="body1" sx={{ mb: 2, color: "black" }}>
                  ご登録のメールアドレスと企業名を入力してください。
                </Typography>
                <form onSubmit={handleRequestReset} autoComplete="off">
                  <TextField
                    label="メールアドレス"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="企業名"
                    type="text"
                    value={resetCompanyName}
                    onChange={(e) => setResetCompanyName(e.target.value)}
                    required
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />

                  {resetMessage && (
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 2,
                        color: resetMessage.includes(
                          "URLをご登録のメールアドレスへ送信しました"
                        )
                          ? "primary.main"
                          : "error.main", // Changed from success.main to primary.main
                      }}
                    >
                      {resetMessage}
                    </Typography>
                  )}
                  <div className="modal-actions">
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting || cooldown > 0}
                      disableRipple
                      sx={{
                        width: "auto", // Auto width
                        whiteSpace: "nowrap",
                        fontWeight: "bold",
                        fontSize: "16px",
                        pl: 2,
                        pr: 2, // Adjusted padding
                        color: "#fff",
                        backgroundColor: "#007bff",
                        border: "4px solid rgba(240, 232, 249, 0.9)",
                        borderRadius: "25px",
                        boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                        "&:hover": {
                          backgroundColor: "#007bff",
                        },
                      }}
                    >
                      {isSubmitting
                        ? "送信中..."
                        : cooldown > 0
                        ? `再送信 (${cooldown}秒)`
                        : "再設定メールを送信"}
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      onClick={() => setIsResetModalOpen(false)}
                      disableRipple
                      sx={{
                        width: "auto", // Auto width
                        whiteSpace: "nowrap",
                        fontWeight: "bold",
                        fontSize: "16px",
                        pl: 4,
                        pr: 4, // Adjusted padding
                        color: "#fff",
                        backgroundColor: "#9e9e9e",
                        border: "4px solid rgba(240, 232, 249, 0.9)",
                        borderRadius: "25px",
                        boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                        "&:hover": {
                          backgroundColor: "#9e9e9e",
                        },
                      }}
                    >
                      キャンセル
                    </Button>
                  </div>
                </form>
              </Box>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
